import React, { useEffect, useState } from 'react';
import { Button, Message, BodyText, Spacer } from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { useForm } from 'react-hook-form';
import { EmailField } from '../../Fields/shared';
import { resetPassword } from '~/helpers/orchestration/password';
import { AxiosError } from 'axios';
import { usePrevious } from '~/App/shared/hooks/usePrevious';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { isEmpty } from '~/App/shared/utils';
import { getSessionStorage } from '~/App/shared/helpers/general';

const StyledMessage = styled(Message)`
  span {
    word-break: break-word;
  }
`;

const convertString = (str: string) => {
  let words = str.split(' ');

  words = words.map(word => {
    if (word?.includes('@')) {
      return `<b>${word}</b>`;
    }

    return word;
  });

  return words.join(' ');
};

const ForgotPasswordForm = () => {
  const [isLoading, setLoading] = useState(false);
  const [isCompleted, setCompleted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { t } = useTranslation();
  const location = useLocation();
  const emailFromSessionStorage = getSessionStorage('email');

  const {
    handleSubmit,
    control,
    getValues,
    formState: { isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: emailFromSessionStorage ?? ''
    }
  });

  const onSubmit = () => {
    setLoading(true);
    setErrors({});
    setCompleted(false);

    const email = getValues().email;

    resetPassword(email)
      .then(() => {
        setCompleted(true);
        setErrors({});
      })
      .catch((error: AxiosError<{ message: Record<string, string> }>) => {
        const defaultError = {
          nonField: t('Something went wrong, please try again later.')
        };
        const errors =
          typeof error.response?.data.message === 'object'
            ? error.response.data.message
            : defaultError;

        setErrors(errors);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const email = getValues().email;
  const prevRouteKey = usePrevious(location.key);

  useEffect(() => {
    if (prevRouteKey !== location.key) {
      setErrors({});
      setCompleted(false);
      setLoading(false);
    }
  }, [prevRouteKey, location.key]);

  if (isCompleted) {
    return (
      <StyledMessage type="success" withIcon fullWidth>
        <span
          dangerouslySetInnerHTML={{
            __html: convertString(
              t(
                'An email has been sent to %1$s if the account exists in our system.',
                email
              )
            )
          }}
        />
      </StyledMessage>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <EmailField control={control} t={t} isDisabled={isLoading} />

        <Spacer height={0.5} />

        <Button
          color="secondary"
          size="regular"
          fullWidth
          isLoading={isLoading}
          isDisabled={isLoading || !isValid}
          type="submit"
        >
          {t('Submit')}
        </Button>
      </form>
      {!isEmpty(errors) && (
        <>
          <Spacer />
          <Message type="error" withIcon fullWidth>
            {Object.keys(errors).map(key => (
              <BodyText key={key}>{errors[key]}</BodyText>
            ))}
          </Message>
        </>
      )}
    </>
  );
};

export default ForgotPasswordForm;
